import React, { useEffect, useState, Fragment } from 'react';
import { Dialog, Transition, Listbox } from '@headlessui/react';
import {
  PlusIcon,
  TrashIcon,
  CheckIcon,
  ChevronUpDownIcon,
  ExclamationTriangleIcon,
  AcademicCapIcon,
  HomeIcon,
  TruckIcon,
  BeakerIcon,
  ExclamationCircleIcon,
  HeartIcon,
  GlobeAltIcon,
  BanknotesIcon,
  StarIcon,
} from '@heroicons/react/24/outline';
import { useData } from '../../context/DataContext';
import { GOAL_COLOR_OPTIONS } from '../../utils/colors';
import toast from 'react-hot-toast';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import Tooltip from '../common/Tooltip';

const iconOptions = [
  { value: 'AcademicCapIcon', label: 'Education', icon: AcademicCapIcon },
  { value: 'HomeIcon', label: 'House', icon: HomeIcon },
  { value: 'TruckIcon', label: 'Car', icon: TruckIcon },
  { value: 'BeakerIcon', label: 'Retirement', icon: BeakerIcon },
  { value: 'ExclamationCircleIcon', label: 'Emergency', icon: ExclamationCircleIcon },
  { value: 'HeartIcon', label: 'Wedding', icon: HeartIcon },
  { value: 'GlobeAltIcon', label: 'Travel', icon: GlobeAltIcon },
  { value: 'BanknotesIcon', label: 'Savings', icon: BanknotesIcon },
  { value: 'StarIcon', label: 'Other', icon: StarIcon },
];

const currencyOptions = [
  { value: 'GBP', label: 'GBP (British Pound)' },
  { value: 'USD', label: 'USD (US Dollar)' },
  { value: 'EUR', label: 'EUR (Euro)' },
  { value: 'JPY', label: 'JPY (Japanese Yen)' },
];

const GoalModal = ({ visible, onCancel, goal }) => {
  const { portfolios, addGoal, updateGoal } = useData();
  const [allocations, setAllocations] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    icon: 'StarIcon',
    currency: 'GBP',
    targetAmount: 0,
    targetDate: '',
    color: GOAL_COLOR_OPTIONS[0].value,
  });
  const [errors, setErrors] = useState({});
  const [deleteDialog, setDeleteDialog] = useState({ open: false, index: null });

  // Close modal on Escape key
  useEscapeKey(onCancel, visible);

  useEffect(() => {
    if (visible) {
      if (goal) {
        setFormData({
          name: goal.name || '',
          icon: goal.icon || goal.emoji || 'StarIcon', // Support legacy emoji field
          currency: goal.currency || 'GBP',
          targetAmount: goal.targetAmount || 0,
          targetDate: goal.targetDate || '',
          color: goal.color || GOAL_COLOR_OPTIONS[0].value,
        });
        setAllocations(goal.allocations || []);
      } else {
        setFormData({
          name: '',
          icon: 'StarIcon',
          currency: 'GBP',
          targetAmount: 0,
          targetDate: '',
          color: GOAL_COLOR_OPTIONS[0].value,
        });
        setAllocations([]);
      }
      setErrors({});
    }
  }, [goal, visible]);

  const handleAddAllocation = () => {
    if (portfolios.length === 0) {
      toast.error('Please create a portfolio account first');
      return;
    }

    const usedPortfolioIds = allocations.map(a => a.portfolioId);
    const availablePortfolio = portfolios.find(p => !usedPortfolioIds.includes(p.id));

    if (!availablePortfolio) {
      toast.error('All portfolios are already allocated');
      return;
    }

    setAllocations([...allocations, {
      portfolioId: availablePortfolio.id,
      type: 'percentage',
      value: 10,
    }]);
  };

  const handleRemoveAllocation = () => {
    if (deleteDialog.index !== null) {
      setAllocations(allocations.filter((_, i) => i !== deleteDialog.index));
      setDeleteDialog({ open: false, index: null });
    }
  };

  const handleAllocationChange = (index, field, value) => {
    const newAllocations = [...allocations];
    newAllocations[index][field] = value;
    setAllocations(newAllocations);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name || formData.name.trim() === '') {
      newErrors.name = 'Please enter goal name';
    }
    if (!formData.icon) {
      newErrors.icon = 'Please select an icon';
    }
    if (!formData.currency) {
      newErrors.currency = 'Please select currency';
    }
    if (!formData.color) {
      newErrors.color = 'Please select a color';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    const goalData = {
      ...formData,
      allocations: allocations,
    };

    if (goal) {
      updateGoal(goal.id, goalData);
    } else {
      addGoal(goalData);
    }

    handleClose();
  };

  const handleClose = () => {
    setFormData({
      name: '',
      icon: 'StarIcon',
      currency: 'GBP',
      targetAmount: 0,
      targetDate: '',
      color: GOAL_COLOR_OPTIONS[0].value,
    });
    setAllocations([]);
    setErrors({});
    onCancel();
  };

  const selectedIcon = iconOptions.find(e => e.value === formData.icon) || iconOptions[0];
  const selectedCurrency = currencyOptions.find(c => c.value === formData.currency) || currencyOptions[0];
  const selectedColor = GOAL_COLOR_OPTIONS.find(c => c.value === formData.color) || GOAL_COLOR_OPTIONS[0];

  return (
    <>
      <Transition appear show={visible} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={handleClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-2xl transform overflow-visible rounded-2xl bg-white p-4 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title as="h3" className="text-base font-semibold text-gray-900 mb-2">
                    {goal ? 'Edit Fund' : 'Create New Fund'}
                  </Dialog.Title>

                  <hr className="mb-3 border-gray-200" />

                  <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="space-y-2">
                    {/* Goal Name */}
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                        Fund Name <span className="text-red-600">*</span>
                      </label>
                      <input
                        id="name"
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="e.g., Ariana Savings"
                        className={`block w-full px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          errors.name ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                    </div>

                    {/* Icon */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Icon <span className="text-red-600">*</span>
                      </label>
                      <Listbox value={selectedIcon} onChange={(value) => setFormData({ ...formData, icon: value.value })}>
                        <div className="relative">
                          <Listbox.Button className={`relative w-full cursor-pointer rounded-lg bg-white py-2 pl-3 pr-10 text-left border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.icon ? 'border-red-500' : 'border-gray-300'
                          }`}>
                            <span className="flex items-center gap-2">
                              <selectedIcon.icon className="h-5 w-5 text-gray-600" />
                              <span className="block truncate">{selectedIcon.label}</span>
                            </span>
                            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                              <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                            </span>
                          </Listbox.Button>
                          <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                            <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                              {iconOptions.map((option) => (
                                <Listbox.Option key={option.value} value={option} className={({ active }) => `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active ? 'bg-blue-100 text-blue-900' : 'text-gray-900'}`}>
                                  {({ selected }) => (
                                    <>
                                      <span className={`flex items-center gap-2 ${selected ? 'font-medium' : 'font-normal'}`}>
                                        <option.icon className="h-5 w-5 text-gray-600" />
                                        {option.label}
                                      </span>
                                      {selected && <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600"><CheckIcon className="h-5 w-5" /></span>}
                                    </>
                                  )}
                                </Listbox.Option>
                              ))}
                            </Listbox.Options>
                          </Transition>
                        </div>
                      </Listbox>
                      {errors.icon && <p className="mt-1 text-sm text-red-600">{errors.icon}</p>}
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      {/* Currency */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Currency <span className="text-red-600">*</span>
                        </label>
                        <Listbox value={selectedCurrency} onChange={(value) => setFormData({ ...formData, currency: value.value })}>
                          <div className="relative">
                            <Listbox.Button className={`relative w-full cursor-pointer rounded-lg bg-white py-2 pl-3 pr-10 text-left border focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.currency ? 'border-red-500' : 'border-gray-300'}`}>
                              <span className="block truncate">{selectedCurrency.label}</span>
                              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2"><ChevronUpDownIcon className="h-5 w-5 text-gray-400" /></span>
                            </Listbox.Button>
                            <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                              <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5">
                                {currencyOptions.map((option) => (
                                  <Listbox.Option key={option.value} value={option} className={({ active }) => `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active ? 'bg-blue-100' : ''}`}>
                                    {({ selected }) => (
                                      <>
                                        <span className={selected ? 'font-medium' : ''}>{option.label}</span>
                                        {selected && <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600"><CheckIcon className="h-5 w-5" /></span>}
                                      </>
                                    )}
                                  </Listbox.Option>
                                ))}
                              </Listbox.Options>
                            </Transition>
                          </div>
                        </Listbox>
                      </div>

                      {/* Color */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Color <span className="text-red-600">*</span>
                        </label>
                        <Listbox value={selectedColor} onChange={(value) => setFormData({ ...formData, color: value.value })}>
                          <div className="relative">
                            <Listbox.Button className={`relative w-full cursor-pointer rounded-lg bg-white py-2 pl-3 pr-10 text-left border focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.color ? 'border-red-500' : 'border-gray-300'}`}>
                              <span className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded" style={{ backgroundColor: selectedColor.hex }}></div>
                                <span>{selectedColor.label}</span>
                              </span>
                              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2"><ChevronUpDownIcon className="h-5 w-5 text-gray-400" /></span>
                            </Listbox.Button>
                            <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                              <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5">
                                {GOAL_COLOR_OPTIONS.map((option) => (
                                  <Listbox.Option key={option.value} value={option} className={({ active }) => `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active ? 'bg-blue-100' : ''}`}>
                                    {({ selected }) => (
                                      <>
                                        <span className="flex items-center gap-2">
                                          <div className="w-6 h-6 rounded" style={{ backgroundColor: option.hex }}></div>
                                          <span className={selected ? 'font-medium' : ''}>{option.label}</span>
                                        </span>
                                        {selected && <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600"><CheckIcon className="h-5 w-5" /></span>}
                                      </>
                                    )}
                                  </Listbox.Option>
                                ))}
                              </Listbox.Options>
                            </Transition>
                          </div>
                        </Listbox>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      {/* Target Amount */}
                      <div>
                        <label htmlFor="targetAmount" className="block text-sm font-medium text-gray-700 mb-1">
                          Target Amount <span className="text-gray-400 font-normal text-xs">(optional)</span>
                        </label>
                        <input
                          id="targetAmount"
                          type="number"
                          min="1"
                          value={formData.targetAmount}
                          onChange={(e) => setFormData({ ...formData, targetAmount: parseFloat(e.target.value) || 0 })}
                          placeholder="e.g., 40000"
                          className={`block w-full px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.targetAmount ? 'border-red-500' : 'border-gray-300'}`}
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Leave blank for a simple earmark with no target
                        </p>
                      </div>
                    </div>

                    <div className="p-2 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-xs text-blue-800 m-0">
                        <strong>Note:</strong> Monthly contributions are now managed at the account level. The contribution for this goal will be calculated based on your account allocations below.
                      </p>
                    </div>

                    {/* Target Date */}
                    <div>
                      <label htmlFor="targetDate" className="block text-sm font-medium text-gray-700 mb-1">
                        Target Date <span className="text-gray-400 font-normal text-xs">(optional)</span>
                      </label>
                      <input
                        id="targetDate"
                        type="date"
                        value={formData.targetDate}
                        onChange={(e) => setFormData({ ...formData, targetDate: e.target.value })}
                        className={`block w-full px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.targetDate ? 'border-red-500' : 'border-gray-300'}`}
                      />
                      {errors.targetDate && <p className="mt-1 text-sm text-red-600">{errors.targetDate}</p>}
                    </div>

                    {/* Portfolio Allocations */}
                    <div className="mt-3">
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center gap-1">
                          <h4 className="text-sm font-bold text-gray-900">Portfolio Allocations</h4>
                          <Tooltip text="Link this goal to specific accounts. You can allocate by percentage of the account or a fixed amount. At least one allocation is required." position="top" />
                        </div>
                        <button
                          type="button"
                          onClick={handleAddAllocation}
                          className="flex items-center gap-1 px-3 py-1.5 text-white rounded-lg text-sm font-medium transition-colors"
                          style={{ backgroundColor: '#012E58' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#01234a'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#012E58'}
                        >
                          <PlusIcon className="w-4 h-4" />
                          <span>Add Allocation</span>
                        </button>
                      </div>

                      {allocations.length === 0 ? (
                        <div className="p-6 text-center text-gray-500 bg-gray-50 rounded-lg">
                          <p className="text-sm">No allocations. Click "Add Allocation" to get started.</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {allocations.map((allocation, index) => {
                            const usedPortfolioIds = allocations.map((a, i) => i !== index ? a.portfolioId : null).filter(Boolean);
                            const availablePortfolios = portfolios.filter(p => !usedPortfolioIds.includes(p.id) || p.id === allocation.portfolioId);
                            const selectedPortfolio = availablePortfolios.find(p => p.id === allocation.portfolioId) || availablePortfolios[0];

                            return (
                              <div key={index} className="bg-gray-100 rounded-lg p-2">
                                <div className="flex gap-2 items-end">
                                  <div className="flex-1">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Account</label>
                                    <Listbox value={selectedPortfolio} onChange={(value) => handleAllocationChange(index, 'portfolioId', value.id)}>
                                      <div className="relative">
                                        <Listbox.Button className="relative w-full cursor-pointer rounded-lg bg-white py-2 pl-3 pr-10 text-left border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500">
                                          <span className="block truncate">{selectedPortfolio?.name} ({selectedPortfolio?.currency})</span>
                                          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2"><ChevronUpDownIcon className="h-5 w-5 text-gray-400" /></span>
                                        </Listbox.Button>
                                        <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                                          <Listbox.Options className="absolute z-50 bottom-full mb-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5">
                                            {availablePortfolios.map((p) => (
                                              <Listbox.Option key={p.id} value={p} className={({ active }) => `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active ? 'bg-blue-100' : ''}`}>
                                                {({ selected }) => (
                                                  <>
                                                    <span className={selected ? 'font-medium' : ''}>{p.name} ({p.currency})</span>
                                                    {selected && <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600"><CheckIcon className="h-5 w-5" /></span>}
                                                  </>
                                                )}
                                              </Listbox.Option>
                                            ))}
                                          </Listbox.Options>
                                        </Transition>
                                      </div>
                                    </Listbox>
                                  </div>

                                  <div className="w-24">
                                    <label className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
                                      <span>Type</span>
                                      <Tooltip text="Choose percentage to allocate a % of the account's value, or fixed amount to allocate a specific amount." position="top" />
                                    </label>
                                    <Listbox value={allocation.type} onChange={(value) => handleAllocationChange(index, 'type', value)}>
                                      <div className="relative">
                                        <Listbox.Button className="relative w-full cursor-pointer rounded-lg bg-white py-2 pl-3 pr-10 text-left border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm">
                                          <span className="block truncate">{allocation.type === 'percentage' ? '%' : 'Value'}</span>
                                          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2"><ChevronUpDownIcon className="h-4 w-4 text-gray-400" /></span>
                                        </Listbox.Button>
                                        <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                                          <Listbox.Options className="absolute z-50 bottom-full mb-1 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 text-sm">
                                            <Listbox.Option value="percentage" className={({ active }) => `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active ? 'bg-blue-100' : ''}`}>
                                              {({ selected }) => (
                                                <>
                                                  <span className={selected ? 'font-medium' : ''}>%</span>
                                                  {selected && <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600"><CheckIcon className="h-4 w-4" /></span>}
                                                </>
                                              )}
                                            </Listbox.Option>
                                            <Listbox.Option value="value" className={({ active }) => `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active ? 'bg-blue-100' : ''}`}>
                                              {({ selected }) => (
                                                <>
                                                  <span className={selected ? 'font-medium' : ''}>Value</span>
                                                  {selected && <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600"><CheckIcon className="h-4 w-4" /></span>}
                                                </>
                                              )}
                                            </Listbox.Option>
                                          </Listbox.Options>
                                        </Transition>
                                      </div>
                                    </Listbox>
                                  </div>

                                  <div className="w-32">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Allocation {allocation.type === 'percentage' ? '%' : formData.currency}</label>
                                    <input
                                      type="number"
                                      min="0"
                                      max={allocation.type === 'percentage' ? "100" : undefined}
                                      value={allocation.value}
                                      onChange={(e) => handleAllocationChange(index, 'value', parseFloat(e.target.value) || 0)}
                                      className="block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                  </div>

                                  <button
                                    type="button"
                                    onClick={() => setDeleteDialog({ open: true, index })}
                                    className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                                  >
                                    <TrashIcon className="w-5 h-5" />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Buttons */}
                    <hr className="my-3 border-gray-200" />
                    <div className="flex gap-3 justify-end">
                      <button
                        type="button"
                        onClick={handleClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
                        style={{ backgroundColor: '#012E58' }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#01234a'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#012E58'}
                      >
                        {goal ? 'Update' : 'Create'}
                      </button>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Delete Allocation Confirmation */}
      <Transition appear show={deleteDialog.open} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setDeleteDialog({ open: false, index: null })}>
          <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex items-center gap-3 mb-4">
                    <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                    <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900">Remove Allocation</Dialog.Title>
                  </div>
                  <p className="text-sm text-gray-500">Are you sure you want to remove this allocation?</p>
                  <div className="mt-6 flex gap-3 justify-end">
                    <button onClick={() => setDeleteDialog({ open: false, index: null })} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                    <button onClick={handleRemoveAllocation} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700">Remove</button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default GoalModal;
